import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import LoadingCenter from '../util/LoadingCenter';
import EmptyState from '../util/EmptyState';

interface Props { access: any; }

const asArray = (d: any) => (Array.isArray(d) ? d : d?.data ?? []);

const getPct = (c: any) => {
  if (!c.lastResult) return 0;
  const score = Number(c.lastResult.score) || 0;
  return score >= (c.passThreshold ?? 80) ? 100 : Math.round(score);
};
const getStatus = (c: any) => {
  const pct = getPct(c);
  return pct >= 100 ? 'completed' : pct > 0 ? 'in_progress' : 'not_started';
};
const STATUS: Record<string, { cls: string; key: string }> = {
  completed: { cls: 'fulfilled', key: 'Completed' },
  in_progress: { cls: 'pending', key: 'InProgress' },
  not_started: { cls: 'new', key: 'NotStarted' },
};

const MobileTraining: React.FC<Props> = () => {
  const { t } = useTranslation();
  const auth = useSelector((state: RootState) => state.auth.value);
  const [courses, setCourses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState<any | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('training/assigned');
      setCourses(asArray(data));
    } catch {
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (auth) load(); }, [auth, load]);

  const filtered = useMemo(
    () => (filter === 'all' ? courses : courses.filter((c) => getStatus(c) === filter)),
    [courses, filter],
  );

  if (loading) return <LoadingCenter />;

  return (
    <div className="flex flex-col gap-[10px]">
      <div className="m-chips">
        {[['all', 'All'], ['in_progress', 'InProgress'], ['completed', 'Completed'], ['not_started', 'NotStarted']].map(([v, k]) => (
          <button type="button" key={v} className={`m-chip${filter === v ? ' on' : ''}`} onClick={() => setFilter(v)}>{t(k)}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="🎓" message={t('NoCourses') as string} />
      ) : (
        <div className="m-list">
          {filtered.map((c) => {
            const st = STATUS[getStatus(c)];
            const pct = getPct(c);
            return (
              <button type="button" key={c.id} className="m-li" style={{ alignItems: 'flex-start' }} onClick={() => setDetail(c)}>
                <span className="m-li-ic" style={{ background: 'rgba(16,185,129,.15)', color: '#059669' }}><FontAwesomeIcon icon={faGraduationCap} /></span>
                <span className="m-li-main">
                  <span className="m-li-title">{c.title || '—'}</span>
                  <span className="m-li-sub">{c.pageCount ?? 0} {t('Pages')} · {c.questionCount ?? 0} {t('Questions')}</span>
                  <span className="m-ts-bar" style={{ marginTop: 8 }}><i style={{ width: `${pct}%` }} /></span>
                </span>
                <span className={`sbadge ${st.cls}`}>{t(st.key)}</span>
              </button>
            );
          })}
        </div>
      )}

      {detail && (
        <>
          <div className="m-sheet-scrim" onClick={() => setDetail(null)} />
          <div className="m-sheet">
            <div className="m-sheet-grab" />
            <div className="m-sheet-head">
              <div>
                <div className="m-sheet-title">{detail.title}</div>
                <div className="m-sheet-sub">{t(STATUS[getStatus(detail)].key)}</div>
              </div>
            </div>
            <div className="m-sheet-body">
              {detail.description && <p className="text-[14px] text-[var(--text-color)] leading-[1.5] mb-[4px]">{detail.description}</p>}
              {[
                [t('Pages'), detail.pageCount],
                [t('Questions'), detail.questionCount],
                [t('PassThreshold'), detail.passThreshold != null ? `${detail.passThreshold}%` : ''],
                [t('Score'), detail.lastResult ? `${detail.lastResult.score}%` : ''],
              ].filter(([, v]) => v || v === 0).map(([label, value], i) => (
                <div key={i} className="m-kv"><span className="m-kv-lbl">{label}</span><span className="m-kv-val">{value}</span></div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileTraining;
